import {Component, computed, inject, OnDestroy, signal, Signal, ViewChild} from '@angular/core';
import {TitleCasePipe} from '@angular/common';
import {ActivatedRoute, RouterModule} from '@angular/router';
import {FormsModule} from '@angular/forms';
import {Apollo} from 'apollo-angular';

import {LogoAreaComponent} from '../logo-area/logo-area.component';
import {HeadlineSectionComponent} from '../headline-section/headline-section.component';
import {DateComponent} from '../../date/date.component';
import {ContextService} from '../../services/context.service';
import {IsAllowedAnywherePipe} from '../../pipes/has-action-pipe';
import {DoButtonComponent} from '../do-button/do-button.component';
import {MessageFieldComponent} from '../do-button/message-field.component';
import {Organization} from '../../types-repository';
import {
  BROWSE_ASPERA_SHARE,
  GET_ASPERA_ENDPOINT,
  GET_ASPERA_PROCESS,
  GET_ASPERA_PROCESSES,
  GET_ASPERA_WORKER_STATUS,
  LOGIN_ASPERA,
  START_ASPERA_BROWSE,
  START_ASPERA_WORKER,
  SUBMIT_ASPERA_MFA,
} from '../../queries-aspera';
import {Endpoint, Entry, Process, WorkerState, WorkerStatus} from '../../types-aspera';
import {ProcessRow, toProcessRow} from './aspera-rows';

/** How many transfers the table shows; older ones stay in the process store. */
export const PROCESS_LIMIT = 30;
/** How often the worker's state is read while it starts, and while it runs (until the hold ends and it is OFF). */
export const WORKER_POLL_MS = 10_000;
export const WORKER_RUNNING_POLL_MS = 60_000;

/** When to read the worker's state again: soon while it starts, now and then while it runs, never once it is off. */
export function workerPollDelay(worker: Pick<WorkerStatus, 'state'> | null): number | null {
  switch (worker?.state) {
    case WorkerState.Starting:
      return WORKER_POLL_MS;
    case WorkerState.Ready:
    case WorkerState.Busy:
      return WORKER_RUNNING_POLL_MS;
    default:
      return null;
  }
}

/** What a browse or login check is waiting for, when it waits for the worker; null when the worker is up. */
export function workerNote(worker: Pick<WorkerStatus, 'state'> | null): string | null {
  if (worker?.state === WorkerState.Off) {
    return 'The worker is off: it starts now, which takes a few minutes.';
  }
  if (worker?.state === WorkerState.Starting) {
    return 'The worker is starting: this runs once its fixed address is attached.';
  }
  return null;
}

/** How often a direct-server listing is polled while its task runs on the endpoint's instance. */
export const LISTING_POLL_MS = 5000;

/** The folder above `path` on the share ("/" stays "/"). */
export function parentFolder(path: string): string {
  const parts = path.split('/').filter(Boolean);
  parts.pop();
  return '/' + parts.join('/');
}

/**
 * Where browsing starts, or null when the endpoint is not browsed. Browsing is its own capability: where our
 * deliveries land, or the share root when this environment only imports from the share.
 */
export function initialFolder(endpoint: Pick<Endpoint, 'browsable' | 'upload'> | null): string | null {
  if (!endpoint?.browsable) {
    return null;
  }
  return endpoint.upload?.remoteRoot ?? '/';
}

/** The entries a finished listing process carries in its data (AWSJSON text). */
export function listingEntries(data: unknown): Entry[] {
  if (typeof data !== 'string' || !data) {
    return [];
  }
  try {
    const parsed = JSON.parse(data);
    return Array.isArray(parsed?.entries) ? parsed.entries : [];
  } catch {
    return [];
  }
}

/** Folders first, then files, each by name -- the order the share's own page uses. */
export function sortEntries(entries: Entry[]): Entry[] {
  return [...entries].sort((a, b) =>
    a.type === b.type ? a.name.localeCompare(b.name) : a.type === 'directory' ? -1 : 1);
}

@Component({
  selector: 'app-aspera-endpoint-detail',
  templateUrl: './aspera-endpoint-detail.component.html',
  styleUrls: ['./aspera-endpoint-detail.component.scss'],
  imports: [LogoAreaComponent, HeadlineSectionComponent, RouterModule, DateComponent, FormsModule,
    IsAllowedAnywherePipe, DoButtonComponent, MessageFieldComponent, TitleCasePipe]
})
export class AsperaEndpointDetailComponent implements OnDestroy {
  readonly processLimit = PROCESS_LIMIT;
  organization: Signal<Organization>;
  endpointId: string;
  endpoint = signal<Endpoint | null>(null);
  processes = signal<ProcessRow[]>([]);
  loading = signal(true);
  error = signal<string | null>(null);
  processesLoading = signal(true);
  processesError = signal<string | null>(null);

  /** The headline renders on first paint with the id, and takes the name once it is known. */
  title = computed(() => this.endpoint()?.name ?? this.endpointId);

  folder = signal<string | null>(null);
  entries = signal<Entry[]>([]);
  browsing = signal(false);
  browseError = signal<string | null>(null);
  /** The fixed-IP worker (Delta DFP); null for an endpoint without one. */
  worker = signal<WorkerStatus | null>(null);
  /** A browse or login check waits for the worker: say so, rather than a bare "running". */
  workerNote = computed(() => workerNote(this.worker()));
  /** A direct server's login is being checked on its worker. */
  loginChecking = signal(false);

  /** Set once login reports MFA_REQUIRED; the code form shows while it is true. Shares never asks today. */
  mfaPending = signal(false);
  mfaCode = '';
  /** Shown for an endpoint whose secret holds no login yet, and when an operator asks to change the login. */
  needsCredentials = signal(false);
  username = '';
  password = '';

  private apollo = inject(Apollo);

  constructor(contextService: ContextService, route: ActivatedRoute) {
    this.organization = contextService.organization;
    this.endpointId = route.snapshot.paramMap.get('endpoint_id')!;
    this.load(true);
    this.loadProcesses();
    this.loadWorker();
  }

  private workerTimer: ReturnType<typeof setTimeout> | null = null;

  /** Read the worker's state, and again per workerPollDelay (one timer, however often called) until it is OFF. */
  loadWorker(): void {
    if (this.destroyed) {
      return;
    }
    this.apollo.use('aspera')
      .query<{ workerStatus: WorkerStatus | null }>({
        query: GET_ASPERA_WORKER_STATUS, variables: {endpointId: this.endpointId}, fetchPolicy: 'network-only',
      })
      .subscribe({
        next: ({data}) => {
          const status = data?.workerStatus ?? null;
          this.worker.set(status);
          this.pollWorker();
        },
        error: () => this.worker.set(null),
      });
  }

  private pollWorker(): void {
    const delay = workerPollDelay(this.worker());
    if (delay !== null && this.workerTimer === null && !this.destroyed) {
      this.workerTimer = setTimeout(() => {
        this.workerTimer = null;
        this.loadWorker();
      }, delay);
    }
  }

  /** Set when the page is left: every polling chain (worker, listing, login check) stops at its next step. */
  private destroyed = false;
  private pollTimers = new Set<ReturnType<typeof setTimeout>>();

  /** A listing's or login check's next poll, cancelled when the page is left. */
  private later(step: () => void, delay: number): void {
    if (this.destroyed) {
      return;
    }
    const timer = setTimeout(() => {
      this.pollTimers.delete(timer);
      step();
    }, delay);
    this.pollTimers.add(timer);
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    if (this.workerTimer !== null) {
      clearTimeout(this.workerTimer);
      this.workerTimer = null;
    }
    this.pollTimers.forEach(timer => clearTimeout(timer));
    this.pollTimers.clear();
  }

  /** Warm the worker and hold it for 30 minutes; a second press only extends the hold. */
  startWorker(button: DoButtonComponent): void {
    this.apollo.use('aspera')
      .mutate<{ startWorker: WorkerStatus }>({mutation: START_ASPERA_WORKER, variables: {endpointId: this.endpointId}})
      .subscribe({
        next: ({data}) => {
          const status = data?.startWorker ?? null;
          this.worker.set(status);
          button.done(status?.message ?? 'worker starting', {severity: 'cl-alert-info'});
          this.pollWorker();
        },
        error: (err) => button.done(err?.message ?? String(err), {severity: 'cl-alert-danger'}),
      });
  }

  /** The endpoint facts and its login status; `browse` the starting folder only on the first load. */
  load(first = false): void {
    this.apollo.use('aspera')
      .query<{ endpoint: Endpoint | null }>({
        query: GET_ASPERA_ENDPOINT,
        variables: {endpointId: this.endpointId},
        fetchPolicy: 'network-only',
      })
      .subscribe({
        next: ({data}) => {
          this.endpoint.set(data?.endpoint ?? null);
          this.loading.set(false);
          // Start where our deliveries land (the share root in prod, /test elsewhere), on the first load only: a
          // reload after a login keeps the folder the operator is in.
          const start = first ? initialFolder(data?.endpoint ?? null) : null;
          if (start !== null) {
            this.open(start);
          }
        },
        error: (err) => {
          this.error.set(err?.message ?? String(err));
          this.loading.set(false);
        }
      });
  }

  /** The Log in button: the password field's Enter key presses it, from outside its template block (the field sits
   * in another @if, where the button's template variable is not in scope). */
  @ViewChild('loginButton') loginButton?: DoButtonComponent;

  /** Enter in the password field: exactly a click on Log in (its spinner, its double-submit guard). */
  loginByEnter(): void {
    this.loginButton?.performAction();
  }

  /** Check the stored login, or set a new one, as signiant's portal login. */
  login(button: DoButtonComponent): void {
    // "Change login" on a logged-in endpoint opens the fields first rather than silently re-checking the stored one.
    if (this.endpoint()?.status?.loggedIn && !this.needsCredentials()) {
      this.username = this.endpoint()?.status?.username ?? '';
      this.password = '';
      this.needsCredentials.set(true);
      button.done('Enter the login to use for this endpoint', {severity: 'cl-alert-info'});
      return;
    }
    // Only sent when the fields are shown; the API stores them once the share accepts them.
    const credentials = this.needsCredentials()
      ? {username: (this.username || '').trim(), password: this.password}
      : {username: null, password: null};
    if (this.needsCredentials() && (!credentials.username || !credentials.password)) {
      button.done('username and password are required', {severity: 'cl-alert-warning'});
      return;
    }
    this.apollo.use('aspera')
      .mutate<{ login: { status: string; message?: string; process?: { id: string } | null } }>({
        mutation: LOGIN_ASPERA, variables: {endpointId: this.endpointId, ...credentials}
      })
      .subscribe({
        next: ({data}) => {
          const result = data?.login;
          if (result?.status === 'MFA_REQUIRED') {
            this.password = '';
            this.needsCredentials.set(false);
            this.mfaPending.set(true);
            button.done(result.message ?? 'enter the MFA code', {severity: 'cl-alert-info'});
          } else if (result?.status === 'NO_CREDENTIALS') {
            this.needsCredentials.set(true);
            button.done(result.message ?? 'enter the endpoint login', {severity: 'cl-alert-warning'});
          } else if (result?.status === 'VERIFYING' && result.process) {
            // A direct server: checked from its fixed-address instance, which can take minutes to start.
            this.password = '';
            this.loginChecking.set(true);
            this.pollLoginCheck(button, result.process.id);
          } else if (result?.status === 'LOGGED_IN') {
            this.password = '';
            this.needsCredentials.set(false);
            button.done('Login accepted by the share', {severity: 'cl-alert-info'});
            this.load();
          } else {
            button.done(result?.message ?? 'login failed', {severity: 'cl-alert-danger'});
          }
        },
        error: (err) => button.done(err?.message ?? String(err), {severity: 'cl-alert-danger'})
      });
  }

  /** Poll a direct server's login check until its task ends; the button keeps spinning meanwhile. */
  pollLoginCheck(button: DoButtonComponent, processId: string): void {
    if (this.destroyed) {
      return;
    }
    this.apollo.use('aspera')
      .query<{ process: Process | null }>({query: GET_ASPERA_PROCESS, variables: {processId}, fetchPolicy: 'network-only'})
      .subscribe({
        next: ({data}) => {
          const process = data?.process;
          if (!process || process.state === 'IN_PROGRESS') {
            this.loadWorker();
            this.later(() => this.pollLoginCheck(button, processId), LISTING_POLL_MS);
            return;
          }
          this.loginChecking.set(false);
          if (process.state === 'SUCCESS') {
            this.needsCredentials.set(false);
            button.done(process.message ?? 'Login accepted by the server', {severity: 'cl-alert-info'});
            this.load();
          } else {
            button.done(process.message ?? 'login check failed', {severity: 'cl-alert-danger'});
          }
        },
        error: (err) => button.done(err?.message ?? String(err), {severity: 'cl-alert-danger'}),
      });
  }

  submitMfa(button: DoButtonComponent): void {
    const code = (this.mfaCode || '').trim();
    if (!code) {
      button.done('enter the code', {severity: 'cl-alert-warning'});
      return;
    }
    this.apollo.use('aspera')
      .mutate<{ submit_mfa: { status: string; message?: string } }>({
        mutation: SUBMIT_ASPERA_MFA, variables: {endpointId: this.endpointId, code}
      })
      .subscribe({
        next: ({data}) => {
          if (data?.submit_mfa?.status === 'LOGGED_IN') {
            this.mfaPending.set(false);
            this.mfaCode = '';
            button.done('Logged in', {severity: 'cl-alert-info'});
            this.load();
          } else {
            button.done(data?.submit_mfa?.message ?? 'code rejected', {severity: 'cl-alert-danger'});
          }
        },
        error: (err) => button.done(err?.message ?? String(err), {severity: 'cl-alert-danger'})
      });
  }

  /** The transfer table on its own: its failure is shown in the table and leaves the rest of the page working. */
  loadProcesses(): void {
    this.processesLoading.set(true);
    this.processesError.set(null);
    this.apollo.use('aspera')
      .query<{ processes: Process[] }>({
        query: GET_ASPERA_PROCESSES,
        variables: {endpointId: this.endpointId, limit: PROCESS_LIMIT},
        fetchPolicy: 'network-only',
      })
      .subscribe({
        next: ({data}) => {
          this.processes.set((data?.processes ?? []).map(toProcessRow));
          this.processesLoading.set(false);
        },
        error: (err) => {
          this.processesError.set(err?.message ?? String(err));
          this.processesLoading.set(false);
        }
      });
  }

  /** List one folder. A live login and every page of the folder, so only on request. */
  open(path: string): void {
    this.folder.set(path);
    this.browsing.set(true);
    this.browseError.set(null);
    this.entries.set([]);
    if (this.endpoint()?.browseAsync) {
      this.openAsync(path);
      return;
    }
    this.apollo.use('aspera')
      .query<{ browse: Entry[] }>({
        query: BROWSE_ASPERA_SHARE,
        variables: {endpointId: this.endpointId, path},
        fetchPolicy: 'network-only',
      })
      .subscribe({
        next: ({data}) => {
          // A slower answer for a folder already left behind must not overwrite the current one.
          if (this.folder() === path) {
            this.entries.set(sortEntries(data?.browse ?? []));
            this.browsing.set(false);
          }
        },
        error: (err) => {
          if (this.folder() === path) {
            this.browseError.set(err?.message ?? String(err));
            this.browsing.set(false);
          }
        }
      });
  }

  /**
   * A direct server is listed by a task on the endpoint's fixed-IP instance: start it, then poll its process until it
   * ends. A cold instance takes minutes, so the row keeps saying "Loading ..." meanwhile.
   */
  openAsync(path: string): void {
    const fail = (err: unknown) => {
      if (this.folder() === path) {
        this.browseError.set((err as {message?: string})?.message ?? String(err));
        this.browsing.set(false);
      }
    };
    this.apollo.use('aspera')
      .mutate<{ startBrowse: { id: string } }>({
        mutation: START_ASPERA_BROWSE,
        variables: {endpointId: this.endpointId, path},
      })
      .subscribe({next: ({data}) => this.pollListing(path, data!.startBrowse.id), error: fail});
  }

  pollListing(path: string, processId: string): void {
    if (this.destroyed || this.folder() !== path) {
      return; // left behind: stop polling
    }
    this.apollo.use('aspera')
      .query<{ process: Process | null }>({
        query: GET_ASPERA_PROCESS,
        variables: {processId},
        fetchPolicy: 'network-only',
      })
      .subscribe({
        next: ({data}) => {
          const process = data?.process;
          if (this.folder() !== path) {
            return;
          }
          if (!process || process.state === 'IN_PROGRESS') {
            this.later(() => this.pollListing(path, processId), LISTING_POLL_MS);
            return;
          }
          if (process.state === 'FAILED') {
            this.browseError.set(process.message ?? 'listing failed');
            this.browsing.set(false);
            return;
          }
          this.entries.set(sortEntries(listingEntries(process.data)));
          this.browsing.set(false);
        },
        error: (err) => {
          if (this.folder() === path) {
            this.browseError.set(err?.message ?? String(err));
            this.browsing.set(false);
          }
        },
      });
  }

  up(): void {
    const folder = this.folder();
    if (folder && folder !== '/') {
      this.open(parentFolder(folder));
    }
  }
}
